/**
 * Client side of Florin's recoverable xDAI bootstrap advance.
 *
 * Railway authenticates and sends the first 0.01 xDAI. Once at least $1 of
 * USDC.e reaches this wallet, the PWA converts that slice into native xDAI,
 * returns 0.02 xDAI (the advance plus a fixed setup fee) to Railway, and
 * retains the remainder as its own gas reserve. IndexedDB state makes every
 * stage resumable.
 */
import { getAddress, keccak256 } from "viem";
import { GAS_SPONSOR, GNOSIS_CHAIN, GNOSIS_GAS, RESERVE_TOKEN } from "./config";
import {
  getGasBootstrapState,
  getGpToken,
  saveGasBootstrapState,
} from "./db";
import { refillGnosisGas } from "./gas";
import * as gp from "./gnosispay/api";
import { getLifiStatus } from "./lifi";
import { gnosisPublicClient, gnosisWalletClient } from "./chains";
import type { GasBootstrapState, Hex } from "./types";
import type { LocalWallet } from "./wallet";

const PROTOCOL_VERSION = 1;
const maintenance = new Map<string, Promise<GasBootstrapState>>();

interface SponsorReply {
  status: "prepared" | "broadcast" | "sent" | "self_funded" | "repaid";
  sponsorAddress: Hex;
  grantAmountWei: string;
  repaymentAmountWei?: string;
  transactionHash: Hex | null;
  repaidTransactionHash: Hex | null;
}

export function bootstrapMessage(walletAddress: Hex, safeAddress: Hex, timestamp: number): string {
  return [
    "Florin gas bootstrap",
    `Version: ${PROTOCOL_VERSION}`,
    `Wallet: ${getAddress(walletAddress)}`,
    `Safe: ${getAddress(safeAddress)}`,
    `Timestamp: ${timestamp}`,
  ].join("\n");
}

export function repaymentMessage(
  walletAddress: Hex,
  safeAddress: Hex,
  transactionHash: Hex,
  timestamp: number,
): string {
  return [
    "Florin gas repayment",
    `Version: ${PROTOCOL_VERSION}`,
    `Wallet: ${getAddress(walletAddress)}`,
    `Safe: ${getAddress(safeAddress)}`,
    `Transaction: ${transactionHash.toLowerCase()}`,
    `Timestamp: ${timestamp}`,
  ].join("\n");
}

async function validGpToken(): Promise<string> {
  await gp.withSession(() => gp.getUser());
  const token = await getGpToken();
  if (!token) throw new Error("Could not establish a Gnosis Pay session");
  return token;
}

async function sponsorFetch(path: string, token: string, body: unknown): Promise<SponsorReply> {
  const response = await fetch(`${GAS_SPONSOR.apiBase}${path}`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(75_000),
  });
  const json = (await response.json().catch(() => ({}))) as SponsorReply & { error?: string };
  if ((!response.ok && response.status !== 202) || !json.status) {
    throw new Error(json.error || `Gas sponsor returned HTTP ${response.status}`);
  }
  return json;
}

function stateFromReply(
  wallet: LocalWallet,
  safeAddress: Hex,
  reply: SponsorReply,
  previous?: GasBootstrapState,
): GasBootstrapState {
  const status =
    reply.status === "self_funded"
      ? "self_funded"
      : reply.status === "repaid"
        ? "repaid"
        : reply.status === "sent"
          ? "sent"
          : "broadcast";
  return {
    ...previous,
    version: 1,
    walletAddress: wallet.address,
    safeAddress,
    status,
    sponsorAddress: getAddress(reply.sponsorAddress) as Hex,
    grantAmountWei: reply.grantAmountWei,
    repaymentAmountWei:
      reply.repaymentAmountWei ?? previous?.repaymentAmountWei ?? reply.grantAmountWei,
    grantTxHash: reply.transactionHash ?? previous?.grantTxHash,
    lastError: undefined,
    updatedAt: Date.now(),
  };
}

async function requestAdvance(
  wallet: LocalWallet,
  safeAddress: Hex,
  previous?: GasBootstrapState,
): Promise<GasBootstrapState> {
  const token = await validGpToken();
  const timestamp = Date.now();
  const signature = await wallet.account.signMessage({
    message: bootstrapMessage(wallet.address, safeAddress, timestamp),
  });
  const reply = await sponsorFetch("/bootstrap", token, {
    walletAddress: wallet.address,
    safeAddress,
    timestamp,
    signature,
  });
  const state = stateFromReply(wallet, safeAddress, reply, previous);
  await saveGasBootstrapState(state);
  return state;
}

async function refillConfirmed(txHash: Hex, serializedTransaction?: Hex): Promise<boolean> {
  const status = await getLifiStatus(txHash, GNOSIS_CHAIN.id, GNOSIS_CHAIN.id).catch(() => null);
  if (status?.status === "DONE") return true;
  if (status?.status === "FAILED" || status?.status === "INVALID") {
    throw new Error("The deposit-to-xDAI conversion failed");
  }
  const client = gnosisPublicClient();
  let receipt = await client.getTransactionReceipt({ hash: txHash }).catch(() => null);
  if (!receipt && serializedTransaction) {
    try {
      await client.sendRawTransaction({ serializedTransaction });
    } catch (error) {
      if (!rawTransactionAlreadyKnown(error)) throw error;
    }
    receipt = await client.getTransactionReceipt({ hash: txHash }).catch(() => null);
  }
  if (receipt?.status === "reverted") throw new Error("The deposit-to-xDAI conversion reverted");
  return receipt?.status === "success";
}

function rawTransactionAlreadyKnown(error: unknown): boolean {
  const message = String(error instanceof Error ? error.message : error).toLowerCase();
  return (
    message.includes("already known") ||
    message.includes("known transaction") ||
    message.includes("nonce too low")
  );
}

async function prepareRepayment(
  wallet: LocalWallet,
  state: GasBootstrapState,
): Promise<GasBootstrapState> {
  if (!state.sponsorAddress || !state.grantAmountWei) throw new Error("Gas sponsor details are missing");
  const repaymentWei = BigInt(state.repaymentAmountWei ?? state.grantAmountWei);
  if (repaymentWei <= 0n) return state;
  const walletClient = gnosisWalletClient(wallet.account);
  const request = await walletClient.prepareTransactionRequest({
    account: wallet.account,
    chain: walletClient.chain,
    to: state.sponsorAddress,
    value: repaymentWei,
  });
  const serialized = await walletClient.signTransaction(request);
  const next: GasBootstrapState = {
    ...state,
    status: "repayment_pending",
    repaymentSerializedTx: serialized,
    repaymentTxHash: keccak256(serialized),
    updatedAt: Date.now(),
  };
  // Persist the exact signed transaction before it can reach the network.
  await saveGasBootstrapState(next);
  return next;
}

async function broadcastRepayment(state: GasBootstrapState): Promise<GasBootstrapState> {
  if (!state.repaymentSerializedTx || !state.repaymentTxHash) {
    throw new Error("Prepared gas repayment is missing");
  }
  const client = gnosisPublicClient();
  const existing = await client
    .getTransactionReceipt({ hash: state.repaymentTxHash })
    .catch(() => null);
  if (!existing) {
    try {
      await client.sendRawTransaction({ serializedTransaction: state.repaymentSerializedTx });
    } catch (error) {
      if (!rawTransactionAlreadyKnown(error)) throw error;
    }
  }
  const receipt =
    existing ??
    (await client.waitForTransactionReceipt({
      hash: state.repaymentTxHash,
      confirmations: 2,
      timeout: 120_000,
    }));
  if (receipt.status !== "success") throw new Error("Gas repayment transaction reverted");
  return state;
}

async function notifyRepayment(wallet: LocalWallet, state: GasBootstrapState): Promise<GasBootstrapState> {
  if (!state.repaymentTxHash) throw new Error("Gas repayment hash is missing");
  const token = await validGpToken();
  const timestamp = Date.now();
  const signature = await wallet.account.signMessage({
    message: repaymentMessage(wallet.address, state.safeAddress, state.repaymentTxHash, timestamp),
  });
  const reply = await sponsorFetch("/repayment", token, {
    walletAddress: wallet.address,
    safeAddress: state.safeAddress,
    transactionHash: state.repaymentTxHash,
    timestamp,
    signature,
  });
  if (reply.status !== "repaid") throw new Error("Gas sponsor has not confirmed repayment yet");
  const next: GasBootstrapState = {
    ...state,
    status: "repaid",
    repaymentSerializedTx: undefined,
    lastError: undefined,
    updatedAt: Date.now(),
  };
  await saveGasBootstrapState(next);
  return next;
}

async function runMaintenance(
  wallet: LocalWallet,
  safeAddress: Hex,
  onProgress?: (label: string) => void,
): Promise<GasBootstrapState> {
  let state = await getGasBootstrapState(wallet.address);
  if (!state || state.safeAddress.toLowerCase() !== safeAddress.toLowerCase()) {
    state = {
      version: 1,
      walletAddress: wallet.address,
      safeAddress,
      status: "requesting",
      updatedAt: Date.now(),
    };
    await saveGasBootstrapState(state);
  }

  try {
    if (!state.sponsorAddress) {
      onProgress?.("Preparing your processing account…");
      state = await requestAdvance(wallet, safeAddress, state);
    }
    if (state.status === "self_funded" || state.status === "repaid") return state;

    const client = gnosisPublicClient();
    const gasBalance = await client.getBalance({ address: wallet.address });
    if (gasBalance < GNOSIS_GAS.bootstrapMinWei) {
      // A persisted transaction may have been evicted from the public
      // mempool. The endpoint is idempotent and will rebroadcast the exact
      // same signed transaction rather than issuing another advance.
      onProgress?.("Preparing your processing account…");
      state = await requestAdvance(wallet, safeAddress, state);
      const refreshedBalance = await client.getBalance({ address: wallet.address });
      if (refreshedBalance < GNOSIS_GAS.bootstrapMinWei) {
        const waiting = { ...state, status: "broadcast" as const, updatedAt: Date.now() };
        await saveGasBootstrapState(waiting);
        return waiting;
      }
    }

    const reserveUnits = await client.readContract({
      address: RESERVE_TOKEN.address,
      abi: [
        {
          type: "function",
          name: "balanceOf",
          stateMutability: "view",
          inputs: [{ name: "owner", type: "address" }],
          outputs: [{ name: "balance", type: "uint256" }],
        },
      ],
      functionName: "balanceOf",
      args: [wallet.address],
    });
    if (reserveUnits < GNOSIS_GAS.topUpUnits) {
      const waiting = { ...state, status: "waiting_deposit" as const, updatedAt: Date.now() };
      await saveGasBootstrapState(waiting);
      return waiting;
    }

    if (!state.refillTxHash) {
      onProgress?.("Reserving network costs from your first deposit…");
      const pending: GasBootstrapState = {
        ...state,
        status: "refill_pending",
        refillUnits: GNOSIS_GAS.topUpUnits.toString(),
        updatedAt: Date.now(),
      };
      await saveGasBootstrapState(pending);
      state = pending;
      await refillGnosisGas(wallet, GNOSIS_GAS.topUpUnits, async (transaction) => {
        state = {
          ...state!,
          refillSerializedTx: transaction.serializedTransaction,
          refillTxHash: transaction.hash,
          updatedAt: Date.now(),
        };
        await saveGasBootstrapState(state);
      });
    } else if (!(await refillConfirmed(state.refillTxHash, state.refillSerializedTx))) {
      return state;
    }

    if (state.refillSerializedTx) {
      state = { ...state, refillSerializedTx: undefined, updatedAt: Date.now() };
      await saveGasBootstrapState(state);
    }

    if (!state.repaymentTxHash) state = await prepareRepayment(wallet, state);
    onProgress?.("Returning the temporary advance and setup fee…");
    state = await broadcastRepayment(state);
    return notifyRepayment(wallet, state);
  } catch (error) {
    const failed: GasBootstrapState = {
      ...state,
      status: "failed",
      lastError: String(error instanceof Error ? error.message : error),
      updatedAt: Date.now(),
    };
    await saveGasBootstrapState(failed);
    throw error;
  }
}

/** Serialise background/manual callers for one wallet. */
export function maintainGasBootstrap(
  wallet: LocalWallet,
  safeAddress: Hex,
  onProgress?: (label: string) => void,
): Promise<GasBootstrapState> {
  const key = wallet.address.toLowerCase();
  const current = maintenance.get(key);
  if (current) return current;
  const run = runMaintenance(wallet, safeAddress, onProgress).finally(() => maintenance.delete(key));
  maintenance.set(key, run);
  return run;
}
